/**
 * The ticked orders of a studio's list (Sales > Business cards, Sales > Logo files), as pure
 * rules: what the box over the column says, which ticks survive a reload, why a box is
 * greyed, and what a delete's answer comes to.
 *
 * A tick lives as long as its row is on the screen. A new page, a new search or a reload
 * drops the ticks of rows that are not there any more: a delete only ever takes orders the
 * desk can see.
 */
import type { OrdersRemoved } from "../../../../lib/cardOrders";

/** What the ticking needs of a row. */
export interface PickableRow {
  id: string;
  /** The server's word: this order may be deleted. */
  removable: boolean;
}

/** The box over the column: the rows of this page that can be ticked, and whether all or some are. */
export function pagePicks(rows: readonly PickableRow[], picked: ReadonlySet<string>): { ids: string[]; all: boolean; some: boolean } {
  const ids = rows.filter((row) => row.removable).map((row) => row.id);
  const on = ids.filter((id) => picked.has(id)).length;
  return { ids, all: ids.length > 0 && on === ids.length, some: on > 0 && on < ids.length };
}

/** The ticks that survive a new list: only rows still on it, and still allowed to go. The same set when nothing changed. */
export function keepPicked(picked: ReadonlySet<string>, rows: readonly PickableRow[]): ReadonlySet<string> {
  const allowed = new Set(rows.filter((row) => row.removable).map((row) => row.id));
  const kept = [...picked].filter((id) => allowed.has(id));
  return kept.length === picked.size ? picked : new Set(kept);
}

/** Why a row's box is greyed: a paid order is a sale; an unpaid one can still be paid. */
export function whyKept(paid: boolean): "sale" | "open" {
  return paid ? "sale" : "open";
}

/** A delete's answer in numbers: how many went, how many stayed, how many test invoices were left behind. */
export function removalCounts(result: OrdersRemoved): { removed: number; kept: number; invoicesLeft: number } {
  return {
    removed: result.removed.length,
    kept: result.refused.length,
    invoicesLeft: result.removed.filter((order) => order.invoiceLeft).length,
  };
}
